import { ReactElement, useCallback, useState } from "react";

import {
  CampaignContactNgpVanImportArgs,
  CampaignContactNgpVanImportResult,
  ImportCampaignContactsFromNgpVanMutation,
  ImportCampaignContactsFromNgpVanMutationVariables,
  NgpVanDatabaseModeGqlType,
  NgpVanIntegration,
} from "~frontend/__generated__/graphql";
import { NGP_VAN_CAMPAIGN_CONTACT_IMPORT } from "~frontend/graphql/campaigns";
import useMutation from "~frontend/util/useMutation";

import { CampaignForEditor } from "../../types";
import ImportForm from "./ImportForm";

export type NgpVanIntegrationDatabaseLabel = { id: string; label: string };

export default function NgpVanImport({
  campaign,
  availableIntegrations,
  readOnly,
  previousJobArgs,
  previousJobResult,
  displayErrorToast,
  resetContactSourceSettings,
}: {
  campaign: CampaignForEditor;
  availableIntegrations: NgpVanIntegration[];
  readOnly?: boolean;
  previousJobArgs?: CampaignContactNgpVanImportArgs;
  previousJobResult?: CampaignContactNgpVanImportResult;
  displayErrorToast: (message: string, errors: unknown) => void;
  resetContactSourceSettings: (() => void) | null;
}): ReactElement {
  const [startImport] = useMutation<
    ImportCampaignContactsFromNgpVanMutation,
    ImportCampaignContactsFromNgpVanMutationVariables
  >(NGP_VAN_CAMPAIGN_CONTACT_IMPORT);

  const ngpVanCampaignIntegrationEnabled = !!(
    campaign.integration && campaign.integration.service == "NGPVAN"
  );

  const startNgpVanImport = useCallback(
    async ({
      integrationIdAndDbMode,
      ngpVanSavedListId,
    }: {
      integrationIdAndDbMode: string;
      ngpVanSavedListId: number;
    }) => {
      const [integrationId, databaseMode] = integrationIdAndDbMode.split("|");
      // TODO: handle missing databaseMode

      const { errors } = await startImport({
        variables: {
          input: {
            campaignId: campaign.id,
            integrationId,
            databaseMode: databaseMode as NgpVanDatabaseModeGqlType,
            savedListId: ngpVanSavedListId,
          },
        },
      });

      if (errors) {
        displayErrorToast(
          "Failed to start NGP VAN / EveryAction import",
          errors
        );
      }
    },
    [campaign, startImport, displayErrorToast]
  );

  const integrationDatabaseTuples = availableIntegrations.reduce(
    (acc: NgpVanIntegrationDatabaseLabel[], integration: NgpVanIntegration) => {
      if (integration.metadata.hasMyCampaign) {
        acc.push({
          id: [integration.id, "MY_CAMPAIGN"].join("|"),
          label: `${integration.name} > MyCampaign`,
        });
      }
      if (integration.metadata.hasMyVoters) {
        acc.push({
          id: [integration.id, "MY_VOTERS"].join("|"),
          label: `${integration.name} > MyVoters`,
        });
      }
      return acc;
    },
    [] as NgpVanIntegrationDatabaseLabel[]
  );

  const campaignIntegrationIdAndDbMode = ngpVanCampaignIntegrationEnabled
    ? [
        campaign.integration?.id,
        campaign.integrationSettings?.databaseMode,
      ].join("|")
    : null;

  const previousJobIntegrationIdAndDbMode =
    previousJobArgs?.integrationId && previousJobArgs?.databaseMode
      ? [previousJobArgs.integrationId, previousJobArgs.databaseMode].join("|")
      : null;

  const initialIntegrationIdAndDbMode = campaignIntegrationIdAndDbMode
    ? campaignIntegrationIdAndDbMode
    : previousJobIntegrationIdAndDbMode
      ? previousJobIntegrationIdAndDbMode
      : integrationDatabaseTuples[0]?.id;

  const [selectedIntegrationAndDbMode, setSlectedIntegrationAndDbMode] =
    useState<string>(initialIntegrationIdAndDbMode);

  return (
    <ImportForm
      onSubmit={readOnly ? undefined : startNgpVanImport}
      availableIntegrationAndDbModes={integrationDatabaseTuples}
      initialValues={{
        integrationIdAndDbMode: initialIntegrationIdAndDbMode,
        ngpVanSavedListId: previousJobArgs?.savedListId ?? "",
        ngpVanSavedListCount: previousJobResult?.sourceList.listCount,
      }}
      readOnly={readOnly}
      resetContactSourceSettings={resetContactSourceSettings}
      selectedIntegrationAndDbMode={selectedIntegrationAndDbMode}
      setSelectedIntegrationAndDbMode={setSlectedIntegrationAndDbMode}
      ngpVanCampaignIntegrationEnabled={ngpVanCampaignIntegrationEnabled}
    />
  );
}
